// Money — pence integers in, formatted strings out. NEVER use floats for
// money arithmetic; the data model stores pence as INTEGER and renderers
// call formatGBP / parseGBP at the boundary. Spec 20 §10 + spec 21 §2.5.
//
// Locale is hardcoded en-GB. NYMR's books are in sterling; we don't ship
// internationalisation in v2.

// Two cached Intl.NumberFormat instances — one for whole-pound output
// ("£0", "£1,200") and one for fractional output ("£0.50", "£399.50").
// Spec 20 §10 wants "£0" not "£0.00" for zero, and "£399.50" not "£399.5"
// for fractional values; a single formatter with maximumFractionDigits:2
// strips trailing zeros (so 39950 renders as "£399.5"). We pick the
// formatter based on whether pence is divisible by 100.
//
// (The literal code snippet in spec 20 §10 falls into this trap. The
// spec text explicitly says 39950 → "£399.50", so we implement the
// intent.)
const gbpWhole = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})
const gbpFractional = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

export function formatGBP(
  pence: number,
  opts?: { signed?: boolean; abs?: boolean }
): string {
  const value = pence / 100
  const fmt = pence % 100 === 0 ? gbpWhole : gbpFractional
  if (opts?.abs) return fmt.format(Math.abs(value))
  const formatted = fmt.format(Math.abs(value))
  if (opts?.signed && value >= 0) return '+' + formatted
  if (value < 0) return '-' + formatted
  return formatted
}

export function formatPercent(
  pct: number,
  opts?: { signed?: boolean }
): string {
  const v = Math.abs(pct).toFixed(1) + '%'
  if (opts?.signed && pct > 0) return '+' + v
  if (pct < 0) return '-' + v
  return v
}

// parseGBP — strip £, commas, whitespace; parse signed decimal; return
// pence as integer. Returns null on anything we can't interpret, so the
// MoneyInput consumer can choose between "clear the field" and "keep the
// last valid value".
//
// Round-trip: parseGBP(formatGBP(p)) === p for any integer p.
//
// Accepts:
//   "£399.50"   "399.50"   "399"    "£1,200"   "1200"   "£1,200.99"
//   "-£300"     "£-300"    "-300"   "+£100"
//   ""          "   "      "£"      "-"        → null
//
// Note: `Number.parseFloat` would mis-handle "300abc" (returns 300). We
// enforce the regex first so that's a clean null.
const NUMERIC = /^[+-]?\d+(\.\d{0,2})?$/
export function parseGBP(input: string): number | null {
  if (typeof input !== 'string') return null
  // Strip £, commas, whitespace. Tolerate £-300 (sign after the symbol) by
  // doing the symbol strip before the numeric check.
  const cleaned = input.replace(/[£,\s]/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '+') return null
  if (!NUMERIC.test(cleaned)) return null
  // parseFloat is safe now that we've gatekept the format.
  const pounds = parseFloat(cleaned)
  if (!Number.isFinite(pounds)) return null
  // Multiply *then* round to nail "£0.10" = 10 pence (0.10 * 100 = 9.999…
  // on some inputs without the round).
  return Math.round(pounds * 100)
}
